/**
 * Logic for saving posts or text fragments to user profile.
 * Includes an inline folder-picker popup on text selection and mobile support.
 */
export const initSaveContent = () => {
    // Check if user is logged in (passed via localization from plugin)
    const isLoggedIn = united_auth_obj?.is_logged_in || false;

    // 1. Save Full Post
    const savePostBtn = document.querySelector('.js-save-post');
    if (savePostBtn) {
        if (!isLoggedIn) {
            // Optional: You could hide it or make it show a login popup
            // For now, satisfy requirements by preventing any 'void' saving
            // savePostBtn.style.display = 'none'; 
        }

        savePostBtn.addEventListener('click', async () => {
            if (!isLoggedIn) {
                alert(united_auth_obj?.i18n?.login_to_save || (united_auth_obj?.i18n?.error_generic || 'Login to save content'));
                return;
            }
            const postId = savePostBtn.getAttribute('data-post-id');
            await performSaveAction({ post_id: postId }, savePostBtn);
        });
    }

    // 2. Save Text Fragment (Selection)
    // Support both Sanctums and Articles
    const contentArea = document.querySelector('.sanctum-content__body') || document.querySelector('.article-content__body');
    if (!contentArea || !isLoggedIn) return;

    // Determine post ID: prefer the save button attribute, fall back to any [data-post-id]
    const resolvePostId = () => {
        const saveBtn = document.querySelector('.js-save-post[data-post-id]');
        if (saveBtn) return saveBtn.getAttribute('data-post-id');

        const el = document.querySelector('[data-post-id]');
        if (el) return el.getAttribute('data-post-id');

        const match = document.body.className.match(/\bpostid-(\d+)\b/);
        return match ? match[1] : null;
    };

    const handleSelection = (e = null) => {
        // Clearing the native text selection when opening the mobile picker fires
        // selectionchange; that must not be interpreted as a request to dismiss it.
        if (folderPicker) return;

        const selection = window.getSelection();
        const selectedText = selection ? selection.toString().trim() : '';

        // Only show if selection is longer than 10 chars and within contentArea
        if (selectedText.length > 10) {
            const range = selection.getRangeAt(0);
            if (contentArea.contains(range.commonAncestorContainer)) {
                // Get HTML instead of plain text to preserve formatting
                const div = document.createElement('div');
                div.appendChild(range.cloneContents());
                const htmlFragment = div.innerHTML;

                showFloatingSaveButton(selection, htmlFragment, resolvePostId, e);
            }
        } else {
            removeFloatingSaveButton();
        }
    };

    // Desktop: trigger on mouseup (pass event to position button near cursor)
    contentArea.addEventListener('mouseup', (e) => {
        // Wait a tiny bit for selection to finalize
        setTimeout(() => handleSelection(e), 10);
    });

    // Mobile: trigger on selectionchange (debounced to avoid flicker)
    let selectionTimeout = null;
    document.addEventListener('selectionchange', () => {
        if (selectionTimeout) clearTimeout(selectionTimeout);
        selectionTimeout = setTimeout(() => {
            // Only handle selectionchange for touch devices to avoid desktop double-trigger
            if (window.matchMedia('(pointer: coarse)').matches) {
                handleSelection();
            }
        }, 200);
    });

    // Hide floating button/picker on outside click
    document.addEventListener('mousedown', (e) => {
        if (floatingBtn && !floatingBtn.contains(e.target)) {
            if (folderPicker && folderPicker.contains(e.target)) return;
            removeFloatingSaveButton();
            removeFolderPicker();
        }
    });
};

// ─── Core Save Action ───────────────────────────────────────────────────────

const performSaveAction = async (data, btn) => {
    const originalContent = btn.innerHTML;
    btn.disabled = true;
    const loadingLabel = united_auth_obj?.i18n?.saving || 'Зберігаємо...';
    btn.innerHTML = `<i class="fa-solid fa-spinner fa-spin"></i> ${btn.classList.contains('js-save-post') ? '<span>' + loadingLabel + '</span>' : ''}`;

    const formData = new FormData();
    formData.append('action', 'united_auth_save_content');
    formData.append('security', united_auth_obj.nonce);
    formData.append('post_id', data.post_id || '');
    if (data.fragment) formData.append('fragment', data.fragment);
    if (data.folder) formData.append('folder', data.folder);

    try {
        const response = await fetch(united_auth_obj.ajax_url, { method: 'POST', body: formData });
        const result = await response.json();

        if (result.success) {
            btn.innerHTML = '<i class="fa-solid fa-check"></i>';
            btn.classList.add('is-saved');
        } else {
            alert(result.data?.message || (united_auth_obj?.i18n?.error_generic || 'Error saving content'));
            btn.innerHTML = originalContent;
            btn.disabled = false;
        }
    } catch (err) {
        console.error('Save failed:', err);
        btn.innerHTML = originalContent;
        btn.disabled = false;
    }
};

// ─── Floating Save Button ────────────────────────────────────────────────────

let floatingBtn = null;
let currentFragment = null;

const showFloatingSaveButton = (selection, html, resolvePostId, e = null) => {
    // If picker is open, don't move the button (could be annoying on mobile)
    if (folderPicker) return;

    if (!floatingBtn) {
        floatingBtn = document.createElement('button');
        floatingBtn.className = 'btn btn--primary btn--sm floating-save-btn';
        floatingBtn.type = 'button';
        floatingBtn.setAttribute('aria-label', united_auth_obj?.i18n?.save_fragment || 'Зберегти фрагмент');
        const label = united_auth_obj?.i18n?.save_fragment || 'Зберегти фрагмент';
        floatingBtn.innerHTML = `<i class="fa-solid fa-bookmark"></i> <span>${label}</span>`;
        document.body.appendChild(floatingBtn);

        floatingBtn.addEventListener('click', async (evt) => {
            evt.stopPropagation();
            const latestSelection = currentFragment;
            const postId = latestSelection?.resolvePostId();
            if (!postId) {
                console.warn('[SaveContent] Could not determine post ID');
                return;
            }
            await showFolderPicker(floatingBtn, latestSelection.html, postId);
        });
    }

    currentFragment = { html, resolvePostId };

    try {
        const isMobile = window.matchMedia('(pointer: coarse)').matches;

        // Position at mouse coords if available (Desktop mouseup)
        if (!isMobile && e && e.pageX && e.pageY) {
            floatingBtn.style.position = 'absolute';
            // Offset a bit above the mouse cursor
            floatingBtn.style.top = `${e.pageY - floatingBtn.offsetHeight - 15}px`;
            floatingBtn.style.left = `${e.pageX - floatingBtn.offsetWidth / 2}px`;
        } else {
            // Selection bounding box (Mobile/other)
            const range = selection.getRangeAt(0);
            const rect = range.getBoundingClientRect();

            floatingBtn.style.position = 'absolute';

            // On mobile, position BELOW the selection to avoid overlapping with native Chrome/Safari menu
            if (isMobile) {
                let top = rect.bottom + window.scrollY + 12;
                // If overflows bottom of viewport, try showing above instead
                if (rect.bottom + 60 > window.innerHeight) {
                    top = rect.top + window.scrollY - floatingBtn.offsetHeight - 12;
                }
                floatingBtn.style.top = `${top}px`;
            } else {
                // Desktop fallback (above selection)
                floatingBtn.style.top = `${rect.top + window.scrollY - floatingBtn.offsetHeight - 12}px`;
            }

            // Center horizontally relative to selection
            let left = rect.left + window.scrollX + rect.width / 2 - floatingBtn.offsetWidth / 2;

            // Keep within screen bounds
            const padding = 10;
            if (left < padding) left = padding;
            if (left + floatingBtn.offsetWidth > window.innerWidth - padding) {
                left = window.innerWidth - floatingBtn.offsetWidth - padding;
            }

            floatingBtn.style.left = `${left}px`;
        }
    } catch (err) { }
};

const removeFloatingSaveButton = () => {
    if (floatingBtn) {
        floatingBtn.remove();
        floatingBtn = null;
    }
};

// ─── Folder Picker Popup ─────────────────────────────────────────────────────

let folderPicker = null;

const showFolderPicker = async (triggerBtn, text, postId) => {
    removeFolderPicker();

    // Clear selection on mobile/touch to hide native browser "Copy/Paste" menu
    if (window.matchMedia('(pointer: coarse)').matches) {
        try {
            window.getSelection().removeAllRanges();
        } catch (e) { }
    }

    const isMobile = window.matchMedia('(pointer: coarse)').matches || window.innerWidth < 600;

    const previousFocus = document.activeElement;
    if (isMobile) {
        const backdrop = document.createElement('div');
        backdrop.className = 'folder-picker-backdrop';
        backdrop.setAttribute('aria-hidden', 'true');
        backdrop.addEventListener('click', removeFolderPicker);
        document.body.appendChild(backdrop);
        document.body.classList.add('has-folder-picker');
    }

    folderPicker = document.createElement('div');
    folderPicker.className = 'folder-picker';
    folderPicker.setAttribute('role', 'dialog');
    folderPicker.setAttribute('aria-modal', isMobile ? 'true' : 'false');
    const pickLabel = united_auth_obj?.i18n?.pick_folder || 'Вибери папку';
    folderPicker.setAttribute('aria-label', pickLabel);

    folderPicker.innerHTML = buildPickerHTML([], 'Завантаження папок…');
    document.body.appendChild(folderPicker);

    if (!isMobile) positionPicker(folderPicker, triggerBtn);
    folderPicker.dataset.previousFocus = previousFocus?.classList?.contains('floating-save-btn') ? 'floating' : '';

    folderPicker.addEventListener('keydown', (event) => {
        if (event.key === 'Escape') {
            event.preventDefault();
            removeFolderPicker();
            return;
        }

        if (event.key === 'Tab') {
            const focusable = [...folderPicker.querySelectorAll('button:not([hidden]):not(:disabled), input:not([disabled])')];
            const first = focusable[0];
            const last = focusable[focusable.length - 1];
            if (event.shiftKey && document.activeElement === first) {
                event.preventDefault();
                last?.focus();
            } else if (!event.shiftKey && document.activeElement === last) {
                event.preventDefault();
                first?.focus();
            }
        }
    });
    folderPicker.querySelector('.folder-picker__close').addEventListener('click', removeFolderPicker);
    folderPicker.querySelector('.folder-picker__option:not(.folder-picker__option--new)')?.focus();

    // ── Folder option click ──
    folderPicker.querySelectorAll('.folder-picker__option:not(.folder-picker__option--new)').forEach(opt => {
        opt.addEventListener('click', async () => {
            await saveFragment(text, postId, opt.dataset.folder);
        });
    });

    // ── New Folder option ──
    const newFolderBtn = folderPicker.querySelector('.folder-picker__option--new');
    const newFolderForm = folderPicker.querySelector('.folder-picker__new-form');
    const newFolderInput = folderPicker.querySelector('.folder-picker__new-input');
    const newFolderConfirm = folderPicker.querySelector('.folder-picker__new-confirm');
    const newFolderCancel = folderPicker.querySelector('.folder-picker__new-cancel');

    if (newFolderBtn) {
        newFolderBtn.addEventListener('click', () => {
            newFolderForm.classList.add('is-visible');
            newFolderBtn.hidden = true;
            newFolderInput.focus();
        });
    }

    newFolderCancel.addEventListener('click', () => {
        newFolderForm.classList.remove('is-visible');
        newFolderBtn.hidden = false;
        newFolderInput.value = '';
        showPickerMessage('');
        newFolderBtn.focus();
    });

    if (newFolderConfirm) {
        newFolderConfirm.addEventListener('click', async () => {
            const name = newFolderInput.value.trim();
            if (!name) {
                showPickerMessage('Введи назву папки.', true);
                newFolderInput.focus();
                return;
            }

            // Create folder first
            newFolderConfirm.disabled = true;
            newFolderConfirm.innerHTML = '<i class="fa-solid fa-spinner fa-spin"></i>';

            const fd = new FormData();
            fd.append('action', 'united_auth_create_folder');
            fd.append('security', united_auth_obj.nonce);
            fd.append('folder_name', name);

            try {
                const response = await fetch(united_auth_obj.ajax_url, { method: 'POST', body: fd });
                const result = await response.json();
                if (!response.ok || !result.success) {
                    throw new Error(result.data?.message || 'Не вдалося створити папку. Перевір назву та спробуй ще раз.');
                }
                const saved = await saveFragment(text, postId, name);
                if (!saved) {
                    throw new Error('Папку створено, але фрагмент не збережено. Спробуй ще раз.');
                }
            } catch (err) {
                console.warn('[FolderPicker] Failed to create folder', err);
                showPickerMessage(err.message || 'Не вдалося створити папку. Спробуй ще раз.', true);
                newFolderConfirm.disabled = false;
                newFolderConfirm.innerHTML = '<i class="fa-solid fa-check" aria-hidden="true"></i>';
            }
        });

        newFolderInput.addEventListener('keydown', async (e) => {
            if (e.key === 'Enter') {
                e.preventDefault();
                newFolderConfirm.click();
            }
        });
    }

    // Show the picker immediately; folder data should never block the save flow.
    const pickerInstance = folderPicker;
    const folderList = pickerInstance.querySelector('.folder-picker__list');
    const newFolderOption = pickerInstance.querySelector('.folder-picker__option--new');
    const loadFolders = async () => {
        try {
            const fd = new FormData();
            fd.append('action', 'united_auth_get_folders');
            fd.append('security', united_auth_obj.nonce);
            const response = await fetch(united_auth_obj.ajax_url, { method: 'POST', body: fd });
            const result = await response.json();
            if (!response.ok || !result.success) {
                throw new Error(result.data?.message || 'Не вдалося завантажити папки. Спробуй ще раз.');
            }
            if (folderPicker !== pickerInstance) return;

            (result.data.folders || []).filter(name => name && name !== 'General').forEach(name => {
                const option = document.createElement('button');
                option.className = 'btn btn--secondary btn--full folder-picker__option';
                option.type = 'button';
                option.dataset.folder = name;
                const icon = document.createElement('i');
                icon.className = 'fa-solid fa-folder';
                icon.setAttribute('aria-hidden', 'true');
                option.append(icon, document.createTextNode(` ${name}`));
                option.addEventListener('click', async () => {
                    await saveFragment(text, postId, option.dataset.folder);
                });
                folderList.insertBefore(option, newFolderOption);
            });

            showPickerMessage('');
            if (!isMobile) positionPicker(pickerInstance, triggerBtn);
        } catch (err) {
            if (folderPicker !== pickerInstance) return;
            console.warn('[FolderPicker] Failed to load folders', err);
            showPickerMessage(err.message || 'Не вдалося завантажити папки. Перевір з’єднання та спробуй ще раз.', true);
        }
    };
    loadFolders();
};

const buildPickerHTML = (folders, foldersError = '') => {
    const i18n = united_auth_obj?.i18n || {};

    const generalOption = `
        <button class="btn btn--secondary btn--full folder-picker__option" data-folder="General" type="button">
            <i class="fa-solid fa-inbox"></i> ${i18n.folder_general || 'General'}
        </button>`;

    const folderOptions = folders
        .filter(f => f && f !== 'General')
        .map(f => `
            <button class="btn btn--secondary btn--full folder-picker__option" data-folder="${escapeAttr(f)}" type="button">
                <i class="fa-solid fa-folder"></i> ${escapeHtml(f)}
            </button>`)
        .join('');

    return `
        <div class="folder-picker__header">
            <div class="folder-picker__heading">
                <span class="folder-picker__eyebrow">Збережений фрагмент</span>
                <p class="folder-picker__title"><i class="fa-solid fa-folder-open" aria-hidden="true"></i> ${i18n.pick_folder || 'Вибери папку'}</p>
            </div>
            <button class="folder-picker__close" type="button" aria-label="Закрити"><i class="fa-solid fa-xmark" aria-hidden="true"></i></button>
        </div>
        <p class="folder-picker__message${foldersError ? ' is-error' : ''}" role="status" aria-live="polite">${escapeHtml(foldersError)}</p>
        <div class="folder-picker__list">
            ${generalOption}
            ${folderOptions}
            <button class="btn btn--secondary btn--full folder-picker__option folder-picker__option--new" type="button">
                <i class="fa-solid fa-plus"></i> ${i18n.new_folder || 'Нова папка...'}
            </button>
        </div>
        <div class="folder-picker__new-form">
            <label class="folder-picker__new-label" for="folder-picker-new-name">${i18n.folder_name_ph || 'Назва папки'}</label>
            <input class="folder-picker__new-input" id="folder-picker-new-name" name="folder_name" type="text" autocomplete="off" placeholder="Назва папки…" maxlength="60" />
            <button class="btn btn--secondary btn--sm folder-picker__new-cancel" type="button">Скасувати</button>
            <button class="btn btn--primary btn--icon folder-picker__new-confirm" type="button" title="${i18n.saving || 'Зберегти'}">
                <i class="fa-solid fa-check" aria-hidden="true"></i>
            </button>
        </div>`;
};

const positionPicker = (picker, anchor) => {
    const anchorRect = anchor.getBoundingClientRect();
    const pickerW = Math.min(320, window.innerWidth - 32);

    let left = anchorRect.left + window.scrollX + anchorRect.width / 2 - pickerW / 2;
    if (left < 8) left = 8;
    if (left + pickerW > window.innerWidth - 8) left = window.innerWidth - pickerW - 8;

    picker.style.position = 'absolute';
    picker.style.width = `${pickerW}px`;
    picker.style.left = `${left}px`;

    requestAnimationFrame(() => {
        const pickerH = picker.offsetHeight;
        let top = anchorRect.top + window.scrollY - pickerH - 10;
        // If it overflows top of window, show below instead
        if (top < window.scrollY + 10) {
            top = anchorRect.top + window.scrollY + anchor.offsetHeight + 10;
            picker.classList.add('is-below');
        }
        picker.style.top = `${top}px`;
    });
};

const removeFolderPicker = () => {
    if (folderPicker) {
        const restoreFocus = folderPicker.dataset.previousFocus === 'floating' ? floatingBtn : null;
        folderPicker.remove();
        folderPicker = null;
        document.querySelector('.folder-picker-backdrop')?.remove();
        document.body.classList.remove('has-folder-picker');
        restoreFocus?.focus();
    }
};

const showPickerMessage = (message, isError = false) => {
    if (!folderPicker) return;
    const messageNode = folderPicker.querySelector('.folder-picker__message');
    messageNode.textContent = message;
    messageNode.classList.toggle('is-error', isError);
};

const saveFragment = async (text, postId, folder) => {
    if (!floatingBtn) return false;
    showPickerMessage('Зберігаємо фрагмент…');
    await performSaveAction({ post_id: postId, fragment: text, folder }, floatingBtn);
    if (floatingBtn.classList.contains('is-saved')) {
        removeFolderPicker();
        setTimeout(removeFloatingSaveButton, 1200);
        return true;
    } else {
        showPickerMessage('Не вдалося зберегти фрагмент. Перевір з’єднання та спробуй ще раз.', true);
        return false;
    }
};

// ─── Helpers ─────────────────────────────────────────────────────────────────

const escapeHtml = (str) => {
    if (typeof str !== 'string') return '';
    return str
        .replace(/&/g, '&amp;')
        .replace(/</g, '&lt;')
        .replace(/>/g, '&gt;')
        .replace(/"/g, '&quot;');
};

const escapeAttr = (str) => {
    if (typeof str !== 'string') return '';
    return escapeHtml(str).replace(/'/g, '&#039;');
};
